import type { PropsWithChildren } from 'react';
import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../meta';
import type { WebsiteTranslatorSettings } from '../../../types';
import { checkBackground } from '../../../utils/check-background';
import { calculateColumnGap } from './calculate-column-gap';
import {
  BACKGROUND_HORIZONTAL_PADDING,
  BACKGROUND_VERTICAL_PADDING
} from './constants';
import { useLayout } from './use-layout';

type BackgroundProps = PropsWithChildren<{
  itemsLength?: number;
}>;

export function Background({ children, itemsLength = 1 }: BackgroundProps) {
  const {
    renderThumbnailMode,
    backgroundColor,
    borderColor,
    cornerRadius,
    font,
    embedType,
    style,
    ...settings
  } = useSettings();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasBackground = checkBackground({
    backgroundColor,
    borderColor,
    renderThumbnailMode
  });

  const stylesWithSmallRowGap = ['style1', 'style3', 'style4', 'style5'];

  useLayout({ containerRef, hasBackground });

  return (
    <Deactivation2
      {...settings}
      appAlias={meta.alias}
      childrenRef={containerRef}
    >
      <Container
        role="region"
        aria-label="Website language selector"
        _renderThumbnailMode={renderThumbnailMode}
        _backgroundColor={backgroundColor}
        _borderColor={borderColor}
        _hasBackground={hasBackground}
        _cornerRadius={cornerRadius}
        _font={font}
        _embedType={embedType}
        _implementSmallRowGap={stylesWithSmallRowGap.includes(style)}
        _columnGap={calculateColumnGap({ style, itemsLength })}
      >
        {children}
      </Container>
    </Deactivation2>
  );
}

const Container = styled.div<{
  _renderThumbnailMode: WebsiteTranslatorSettings['renderThumbnailMode'];
  _backgroundColor: WebsiteTranslatorSettings['backgroundColor'];
  _borderColor: WebsiteTranslatorSettings['borderColor'];
  _hasBackground: boolean;
  _cornerRadius: WebsiteTranslatorSettings['cornerRadius'];
  _font: WebsiteTranslatorSettings['font'];
  _embedType: WebsiteTranslatorSettings['embedType'];
  _implementSmallRowGap: boolean;
  _columnGap: number;
}>`
  ${({
    _backgroundColor,
    _borderColor,
    _hasBackground,
    _cornerRadius,
    _font,
    _embedType,
    _implementSmallRowGap,
    _columnGap
  }) => css`
    display: inline-flex;
    flex-wrap: wrap;
    row-gap: ${_implementSmallRowGap ? 4 : 10}px;
    column-gap: ${_columnGap}px;
    ${_hasBackground &&
    css`
      padding: ${BACKGROUND_VERTICAL_PADDING}px
        ${BACKGROUND_HORIZONTAL_PADDING}px;
    `}
    ${_backgroundColor &&
    css`
      background-color: ${_backgroundColor};
    `}
    ${_borderColor &&
    css`
      border: 1px solid ${_borderColor};
    `}
    ${_font.value &&
    css`
      font-family: ${_font.value};
    `};
    ${_hasBackground &&
    _cornerRadius &&
    css`
      border-radius: ${_cornerRadius}px;
    `}

    ${_embedType === 'floating' &&
    css`
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.3);
    `}
  `}

  ${({ _renderThumbnailMode, _backgroundColor, _borderColor }) =>
    _renderThumbnailMode &&
    css`
      box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.08);

      ${(!_backgroundColor || _backgroundColor === 'transparent') &&
      css`
        background-color: #fff;
      `}

      ${!checkBackground({
        backgroundColor: _backgroundColor,
        borderColor: _borderColor
      }) &&
      css`
        border-radius: 8px;
      `}
    `}
`;
